import * as THREE from 'three';
import {Thumbnail} from '../segment-parser/element';

export class Hud {
    domElement: HTMLCanvasElement;
    context: CanvasRenderingContext2D;
    size: number;

    protected clickRequired: boolean;
    protected thumbnail?: Thumbnail;
    protected needsRender: boolean;
    protected _visible: boolean;
    protected _highlight: boolean;

    constructor() {
        this.domElement = document.createElement('canvas');
        this.domElement.style.cssText = 'position:fixed;top:0;left:0;cursor:pointer;z-index:1000';

        let context = this.domElement.getContext('2d');
        if (context === null) {
            throw new Error("Cannot get 2D context from canvas");
        }

        this.context = context;
        this.size = 10;
        this.needsRender = true;
        this.clickRequired = true;

        this._visible = true;
        this._highlight = false;
    }

    isVisible(): boolean {
        return this._visible;
    }

    setVisible(visible: boolean) {
        if (this._visible === visible) {
            return;
        }

        this._visible = visible;
        this.needsRender = true;
    }

    setClickRequired(required: boolean) {
        if (this.clickRequired === required) {
            return;
        }

        this.clickRequired = required;
        this.needsRender = true;
    }

    isHighlight(): boolean {
        return this._highlight;
    }

    setHighlight(highlight: boolean) {
        if (this._highlight === highlight) {
            return;
        }

        this._highlight = highlight;
        this.needsRender = true;
    }

    setThumbnail(thumbnail?: Thumbnail) {
        if (this.thumbnail !== thumbnail) {
            this.thumbnail = thumbnail;
            this.needsRender = true;
        }
    }

    render() {
        if (!this.needsRender) {
            return;
        }

        this.needsRender = false;
        this.context.clearRect(0, 0, this.domElement.width, this.domElement.height);

        if (!this._visible) {
            return;
        }

        if (this.clickRequired) {
            this.renderClickRequired();
            return;
        }

        this.drawCursor();
        this.drawThumbnail();
    }

    renderClickRequired() {
        this.context.clearRect(0, 0, this.domElement.width, this.domElement.height);
        this.context.fillStyle = 'rgba(255, 255, 255, 0.5)';
        this.context.fillRect(0, 0, this.domElement.width, this.domElement.height);
        this.context.fillStyle = 'rgba(255, 255, 255, 1.0)';
        this.context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
        this.context.lineWidth = 2;
        let size = 50;
        this.context.font = size + 'px Arial';
        let text = 'Click here to start the interaction';
        let width = this.context.measureText(text).width;
        this.context.fillText(text,
            this.domElement.width / 2 - width / 2,
            this.domElement.height / 2 - size / 2,
        );
        this.context.strokeText(text,
            this.domElement.width / 2 - width / 2,
            this.domElement.height / 2 - size / 2,
        );
    }

    drawCursor() {
        let i = this.domElement.width / 2;
        let imin = i - this.size;
        let imax = i + this.size;

        let j = this.domElement.height / 2;
        let jmin = j - this.size;
        let jmax = j + this.size;

        this.context.beginPath();
        this.context.moveTo(imin, j);
        this.context.lineTo(imax, j);
        this.context.moveTo(i, jmin);
        this.context.lineTo(i, jmax);
        this.context.closePath();

        this.context.lineWidth = 5;
        this.context.strokeStyle = '#ffffff';
        this.context.stroke();

        this.context.lineWidth = 2;

        this.context.strokeStyle = this._highlight ? "#ff0000" : "#000000";
        this.context.stroke();
    }

    drawThumbnail() {
        if (this.thumbnail === undefined) {
            return;
        }

        let t = this.thumbnail.bitmap;
        let aspectRatio = t.width / t.height;

        this.context.drawImage(
            t,
            0.02 * this.domElement.width,
            (1 - 0.02 - 0.2) * this.domElement.height,
            0.2 * this.domElement.height * aspectRatio,
            0.2 * this.domElement.height,
        );

        this.context.strokeStyle = "#000000";
        this.context.beginPath();
        this.context.rect(
            0.02 * this.domElement.width,
            (1 - 0.02 - 0.2) * this.domElement.height,
            0.2 * this.domElement.height * aspectRatio,
            0.2 * this.domElement.height,
        );
        this.context.stroke();

        this.context.strokeStyle = "#ffffff";
        this.context.beginPath();
        this.context.rect(
            0.02 * this.domElement.width + 2,
            (1 - 0.02 - 0.2) * this.domElement.height + 2,
            0.2 * this.domElement.height * aspectRatio - 4,
            0.2 * this.domElement.height - 4,
        );
        this.context.stroke();
    }

    accept(_event: PointerEvent): boolean {
        return true;
    }

    setSize(width: number, height: number) {
        this.domElement.width = width;
        this.domElement.height = height;
        this.needsRender = true;
    }
}

export class NaiveMobileHud extends Hud {
    radius: number;
    margin: number;
    maxNorm: number;
    joystickFinger: THREE.Vector2;
    joystickEventId?: number;
    rotationSensitivity: number;
    cameraRotationEventId?: number;
    cameraRotation: THREE.Vector2;

    constructor() {
        super();

        this.radius = 0;
        this.margin = 0;
        this.maxNorm = 0;
        this.joystickFinger = new THREE.Vector2();
        this.cameraRotation = new THREE.Vector2();
        this.rotationSensitivity = 0.001;
    }

    accept(event: PointerEvent): boolean {
        if (this.isVisible() && !this.clickRequired) {
            if (event.offsetX < 2 * this.margin + 2 * this.radius) {
                if (event.offsetY > this.domElement.height - 2 * this.margin - 2 * this.radius) {
                    return false;
                }
            }

            return true;
        }

        return false;
    }

    onPointerDown(event: PointerEvent): THREE.Vector2 {
        if (this.isVisible() && !this.clickRequired) {
            if (event.offsetX < 2 * this.margin + 2 * this.radius) {
                if (event.offsetY > this.domElement.height - 2 * this.margin - 2 * this.radius) {
                    this.joystickEventId = event.pointerId;
                    return this.onPointerMove(event);
                }
            }

            this.cameraRotationEventId = event.pointerId;
        }

        return new THREE.Vector2();
    }

    onPointerUp(event: PointerEvent): THREE.Vector2 {
        switch (event.pointerId) {
            case this.joystickEventId:
                this.joystickEventId = undefined;
                this.joystickFinger.x = 0;
                this.joystickFinger.y = 0;
                this.needsRender = true;
                break;

            case this.cameraRotationEventId:
                this.cameraRotationEventId = undefined;
                this.needsRender = true;
                break;
        }

        return this.direction();
    }

    onPointerMove(event: PointerEvent): THREE.Vector2 {
        switch (event.pointerId) {
            case this.joystickEventId:
                this.joystickFinger.set(0, this.maxNorm);
                break;

            case this.cameraRotationEventId:
                this.cameraRotation.y += event.movementY * this.rotationSensitivity;
                this.cameraRotation.x += event.movementX * this.rotationSensitivity;
                break;
        }

        return this.direction();
    }


    direction(): THREE.Vector2 {
        return new THREE.Vector2(
            this.joystickFinger.x / this.maxNorm,
            this.joystickFinger.y / this.maxNorm,
        );
    }

    setSize(width: number, height: number) {
        super.setSize(width, height);
        this.radius = width / 10;
        this.margin = width / 20;
        this.maxNorm = this.radius;
        this.needsRender = true;
    }

    render() {
        if (!this.needsRender) {
            return;
        }

        if (this.clickRequired) {
            this.context.clearRect(0, 0, this.domElement.width, this.domElement.height);
            this.context.fillStyle = 'rgba(255, 255, 255, 0.5)';
            this.context.fillRect(0, 0, this.domElement.width, this.domElement.height);
            this.context.fillStyle = 'rgba(255, 255, 255, 1.0)';
            this.context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
            this.context.lineWidth = 2;
            let size = 25;
            this.context.font = size + 'px Arial';
            let text = 'Click here to start the interaction';
            let width = this.context.measureText(text).width;
            this.context.fillText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2,
            );
            this.context.strokeText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2,
            );

            text = 'Be careful to your mobile data!';
            width = this.context.measureText(text).width;
            this.context.fillText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2 + 2 * size,
            );
            this.context.strokeText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2 + 2 * size,
            );
            return;
        }

        this.context.clearRect(0, 0, this.domElement.width, this.domElement.height);

        // Renders the joystick
        if (this.isVisible()) {
            this.context.fillStyle = 'rgba(255, 255, 255, 0.7)';
            this.context.beginPath();
            this.context.arc(
                this.margin + this.radius,
                this.domElement.height - this.margin - this.radius,
                this.radius,
                0, 2 * Math.PI
            );
            this.context.fill();

            this.context.fillStyle = 'rgba(0, 0, 0, 0.7)';
            this.context.beginPath();

            let l = 0.5 * this.radius;
            this.context.moveTo(this.margin + this.radius - l, this.domElement.height - (this.margin + this.radius - l));
            this.context.lineTo(this.margin + this.radius + l, this.domElement.height - (this.margin + this.radius - l));
            this.context.lineTo(this.margin + this.radius,     this.domElement.height - (this.margin + this.radius + l));
            this.context.lineTo(this.margin + this.radius - l, this.domElement.height - (this.margin + this.radius - l));
            this.context.fill();
        }

        this.needsRender = false;
    }

}

export class MobileHud extends NaiveMobileHud {

    constructor() {
        super();
    }

    onPointerMove(event: PointerEvent): THREE.Vector2 {
        switch (event.pointerId) {
            case this.joystickEventId:
                this.joystickFinger.x = event.offsetX - this.margin - this.radius;
                this.joystickFinger.y = -(event.offsetY - this.domElement.height + this.radius + this.margin);

                let norm = Math.sqrt(this.joystickFinger.x * this.joystickFinger.x + this.joystickFinger.y * this.joystickFinger.y)
                if (norm > this.maxNorm) {
                    this.joystickFinger.x *= (this.maxNorm / norm);
                    this.joystickFinger.y *= (this.maxNorm / norm);
                }
                this.needsRender = true;
                break;

            case this.cameraRotationEventId:
                this.cameraRotation.y += event.movementY * this.rotationSensitivity;
                this.cameraRotation.x += event.movementX * this.rotationSensitivity;
                break;
        }

        return this.direction();
    }


    render() {
        if (!this.needsRender) {
            return;
        }

        if (this.clickRequired) {
            this.context.clearRect(0, 0, this.domElement.width, this.domElement.height);
            this.context.fillStyle = 'rgba(255, 255, 255, 0.5)';
            this.context.fillRect(0, 0, this.domElement.width, this.domElement.height);
            this.context.fillStyle = 'rgba(255, 255, 255, 1.0)';
            this.context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
            this.context.lineWidth = 2;
            let size = 25;
            this.context.font = size + 'px Arial';
            let text = 'Click here to start the interaction';
            let width = this.context.measureText(text).width;
            this.context.fillText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2,
            );
            this.context.strokeText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2,
            );

            text = 'Be careful to your mobile data!';
            width = this.context.measureText(text).width;
            this.context.fillText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2 + 2 * size,
            );
            this.context.strokeText(text,
                this.domElement.width / 2 - width / 2,
                this.domElement.height / 2 - size / 2 + 2 * size,
            );
            return;
        }

        this.context.clearRect(0, 0, this.domElement.width, this.domElement.height);

        // Renders the joystick
        if (this.isVisible()) {
            this.context.fillStyle = 'rgba(255, 255, 255, 0.7)';
            this.context.beginPath();
            this.context.arc(
                this.margin + this.radius,
                this.domElement.height - this.margin - this.radius,
                this.radius,
                0, 2 * Math.PI
            );
            this.context.fill();

            this.context.fillStyle = 'rgba(0, 0, 0, 0.7)';
            this.context.beginPath();
            this.context.arc(
                this.margin + this.radius + this.joystickFinger.x,
                this.domElement.height - this.margin - this.radius - this.joystickFinger.y,
                this.radius / 2,
                0, 2 * Math.PI
            );
            this.context.fill();
        }

        this.needsRender = false;
    }
}
